"use client";

import { PhoneVerificationError } from "@repo/common/phone-errors";
import { OTPInput, type SlotProps } from "input-otp";
import { Loader2 } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Country, Value as PhoneValue } from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { toast } from "sonner";

import { PhoneInput } from "@/components/ui/phone-input";
import { useCountryAvailability } from "@/hooks/use-country-availability";
import {
  canTwilioReachPhone,
  friendlyPhoneError,
  readErrorCountry,
} from "@/lib/phone-errors";
import { api } from "@/lib/trpc/react";
import { cn } from "@/lib/utils";

import { JoinStepBar } from "./join-step-bar";

// qr-code-styling is DOM-only (~25KB). Keep it out of the main bundle for
// users who fall back to the SMS code flow.
const StyledQR = dynamic(
  () => import("@/components/ui/styled-qr").then((m) => m.StyledQR),
  { ssr: false },
);

/**
 * Server-resolved signup-time UTM attribution. Plumbed through page.tsx →
 * JoinFlow so the PostHog `onboarding_join_phone_completed` event carries
 * acquisition-channel context without an extra round-trip. Mirrors
 * User.signupUtm* columns.
 */
export interface SignupUtm {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
}

interface JoinPhoneStepProps {
  returnUrl?: string;
  signupUtm?: SignupUtm | null;
}

type SubStep = "phone" | "qr" | "code";

/**
 * Country name in the reader's language.
 *
 * Was `new Intl.DisplayNames(["en"], ...)` at four call sites, which rendered
 * "Spain" on a Spanish page. The locale is passed in rather than read from a
 * hook because this is called inside JSX in several components.
 */
function countryName(locale: string, country: Country, fallback: string) {
  try {
    return (
      new Intl.DisplayNames([locale], { type: "region" }).of(country) ??
      fallback
    );
  } catch {
    // An unrecognised region code makes `of` throw; the copy still has to read.
    return fallback;
  }
}

function detectIOS(): boolean {
  return (
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod/.test(navigator.userAgent)
  );
}

function buildDeepLink(
  scheme: "imessage" | "sms",
  linqNumber: string,
  token: string,
): string {
  const body = `Hi Terac! My code is ${token}.`;
  return `${scheme}:${linqNumber}?body=${encodeURIComponent(body)}`;
}

// How long the Linq hand-off gets before the Twilio code path is offered.
// The two paths are not interchangeable: a Twilio-verified number carries no
// `linqAssignedLine`, so taking the exit costs us the ability to message that
// participant at all. Long enough to clear the one-tap mobile path and most of
// a desktop scan, short enough that a stuck user is not stranded, and well
// inside the token's 10-minute life so the exit stays usable once shown.
const CODE_FALLBACK_DELAY_MS = 45 * 1000;

export function JoinPhoneStep({ returnUrl, signupUtm }: JoinPhoneStepProps) {
  const tErr = useTranslations("Join.errors");
  const router = useRouter();
  const posthog = usePostHog();
  // onboarding_join_phone_completed fires from /join only; the underlying
  // verifyPhoneCode use-case is shared with settings, so the capture lives here
  // at the /join caller to keep the funnel scoped.
  const phoneCompletedFiredRef = useRef(false);
  // QR is the entry point; the user types their number on the QR screen, then
  // verifies straight from their phone. Fallback path (Twilio code) routes
  // through "phone" → "code" if the QR isn't working for them.
  const [step, setStep] = useState<SubStep>("qr");
  const [phoneNumber, setPhoneNumber] = useState<PhoneValue>();
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationData, setVerificationData] = useState<{
    verificationSid: string;
    verifyServiceSid: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country>("US");
  const [resendIn, setResendIn] = useState(0);
  const [smsRetryAt, setSmsRetryAt] = useState(0);
  const [additionalVerificationRequired, setAdditionalVerificationRequired] =
    useState(false);

  const utils = api.useUtils();

  // Linq messages-based verification state.
  const [linqToken, setLinqToken] = useState<string | null>(null);
  const [linqNumber, setLinqNumber] = useState<string | null>(null);
  const [qrTimedOut, setQrTimedOut] = useState(false);
  // Remaining lifetime in ms, as the server measured it.
  const [linqExpiresInMs, setLinqExpiresInMs] = useState<number | null>(null);
  // The number the active token is bound to. Keyed on it so the mint effect is
  // a no-op under React strict-mode double-invocation (same number → skip) yet
  // re-mints when the user corrects their number (the old binding would reject
  // the new sender at the webhook).
  const lastMintedPhoneRef = useRef<string | null>(null);
  // Latches on: once the user has earned the code path we never take it back,
  // so stepping out to it and returning does not restart the wait.
  const [codeFallbackOffered, setCodeFallbackOffered] = useState(false);
  // A mint that errored. Distinct from an expired token: nothing was ever
  // issued, so neither the reveal timer nor the expiry timer is armed and the
  // screen has nothing on it to press.
  const [mintFailed, setMintFailed] = useState(false);

  useEffect(() => {
    const update = () => {
      setResendIn(Math.max(0, Math.ceil((smsRetryAt - Date.now()) / 1000)));
    };
    update();
    if (smsRetryAt <= Date.now()) {
      return;
    }
    const timer = setInterval(() => {
      update();
      if (smsRetryAt <= Date.now()) {
        clearInterval(timer);
      }
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [smsRetryAt]);

  // Policy and capability are answered separately: `countryAvailable` decides
  // whether we say "not available in <country>" at all, the channel flags only
  // decide which verification path to offer. Much of Africa is Twilio-blocked
  // but Linq-reachable, and must still get the QR rather than a "not launched
  // here" message.
  const {
    available: countryAvailable,
    canUseLinq,
    canUseTwilio,
  } = useCountryAvailability(selectedCountry);

  const linqCountrySupported = countryAvailable && canUseLinq;
  const isTwilioCountrySupported =
    countryAvailable && canUseTwilio && canTwilioReachPhone(phoneNumber);

  const isMobile =
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- `window?.`/`navigator?.` still throws when the global is undeclared; the typeof guard is the point
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches;

  /* ─────────────────────────────────────────────────────────────────── */
  /* Linq messages flow (default path)                                    */
  /* ─────────────────────────────────────────────────────────────────── */

  // The QR always uses the universal `sms:` scheme (we don't run the iMessage
  // availability probe on this screen). Mobile auto-picks `imessage:` later via
  // `detectIOS()` because we KNOW which device opens the deep link there.
  const preferredScheme: "imessage" | "sms" = "sms";

  const startMessages = api.user.startMessagesVerification.useMutation({
    onSuccess: (result) => {
      setLinqToken(result.token);
      setLinqNumber(result.linqNumber);
      setLinqExpiresInMs(result.expiresInMs);
      setError(null);
      setQrTimedOut(false);
      setMintFailed(false);
    },
    onError: (err) => {
      // Both timers hang off the token, so a failed mint arms neither. Offer
      // the exits now or the user is stranded on a placeholder for good.
      setMintFailed(true);
      setCodeFallbackOffered(true);
      setError(
        friendlyPhoneError(
          err.message ||
            "We couldn't start phone verification. Please try again.",
          readErrorCountry(err),
        ),
      );
    },
  });

  const isPhoneReadyForLinq = Boolean(
    phoneNumber && isValidPhoneNumber(phoneNumber) && linqCountrySupported,
  );

  // Mint (or re-mint) the bound token once the user has a valid, supported
  // number that differs from the one the current token was minted for. The
  // server binds the token to this exact number and the webhook rejects any
  // other sender, so we must never mint before a valid number exists and must
  // re-bind if the user corrects their number.
  useEffect(() => {
    if (!isPhoneReadyForLinq || !phoneNumber) {
      return;
    }
    if (lastMintedPhoneRef.current === phoneNumber) {
      return;
    }
    lastMintedPhoneRef.current = phoneNumber;
    setLinqToken(null);
    setLinqNumber(null);
    setLinqExpiresInMs(null);
    setQrTimedOut(false);
    startMessages.mutate({ phoneNumber });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mint keyed on the bound number
  }, [isPhoneReadyForLinq, phoneNumber]);

  // Keyed on the token, not on mount: the mint is a server round trip, and
  // charging its latency to the user would offer the exit before the QR they
  // are meant to scan has rendered.
  useEffect(() => {
    if (!linqToken || codeFallbackOffered) {
      return;
    }
    const t = setTimeout(() => {
      setCodeFallbackOffered(true);
    }, CODE_FALLBACK_DELAY_MS);
    return () => {
      clearTimeout(t);
    };
  }, [linqToken, codeFallbackOffered]);

  // Two cases skip the wait entirely, because waiting cannot help. Linq is
  // blocked in this country, so no token is ever minted and the timer above
  // would never fire; or the token already expired, and "Regenerate code" on
  // its own is a loop with no exit for a handset the SMS never reaches.
  // A failed mint latches `codeFallbackOffered` for the same reason.
  const showCodeFallback =
    !additionalVerificationRequired &&
    isTwilioCountrySupported &&
    (codeFallbackOffered || !linqCountrySupported || qrTimedOut);

  const sendVerificationMutation = api.user.sendPhoneVerification.useMutation({
    onSuccess: (response) => {
      if (
        response &&
        typeof response === "object" &&
        "success" in response &&
        response.success &&
        "verificationSid" in response &&
        "verifyServiceSid" in response
      ) {
        setError(null);
        setVerificationData({
          verificationSid: String(response.verificationSid),
          verifyServiceSid: String(response.verifyServiceSid),
        });
        setStep("code");
        setSmsRetryAt(Date.now() + 60_000);
      } else if (
        response &&
        typeof response === "object" &&
        "success" in response &&
        !response.success &&
        "error" in response
      ) {
        if (response.retryAfterSeconds !== undefined) {
          setSmsRetryAt(Date.now() + response.retryAfterSeconds * 1000);
        }
        if (
          response.error ===
          PhoneVerificationError.ADDITIONAL_VERIFICATION_REQUIRED
        ) {
          setAdditionalVerificationRequired(true);
          setStep("qr");
          setError(null);
          return;
        }
        setError(
          friendlyPhoneError(
            typeof response.error === "string"
              ? response.error
              : "Unable to send code. Check the number and try again.",
            "errorCountry" in response &&
              typeof response.errorCountry === "string"
              ? response.errorCountry
              : undefined,
            response.retryAfterSeconds,
          ),
        );
      } else {
        setError("Something went wrong. Please try again.");
      }
    },
    onError: (err: unknown) => {
      setError(
        friendlyPhoneError(
          err instanceof Error
            ? err.message
            : "There was a problem reaching our SMS service. Please try again.",
        ),
      );
    },
  });

  const linkAccountsMutation = api.user.linkAccountsByPhone.useMutation({
    onSuccess: () => {
      const target = returnUrl
        ? `/join?returnUrl=${encodeURIComponent(returnUrl)}`
        : "/join";
      router.push(target);
      router.refresh();
    },
    onError: (err: unknown) => {
      toast.error(tErr("accountLinking"), {
        description:
          (err instanceof Error ? err.message : null) ||
          tErr("accountLinkingDescription"),
      });
      const target = returnUrl
        ? `/join?returnUrl=${encodeURIComponent(returnUrl)}`
        : "/join";
      router.push(target);
      router.refresh();
    },
  });

  const verifyCodeMutation = api.user.verifyPhoneCode.useMutation({
    onSuccess: (response) => {
      if (
        response &&
        typeof response === "object" &&
        "success" in response &&
        response.success &&
        phoneNumber
      ) {
        if (!phoneCompletedFiredRef.current) {
          phoneCompletedFiredRef.current = true;
          posthog?.capture("onboarding_join_phone_completed", {
            method: "code",
            utm_source: signupUtm?.source ?? null,
            utm_medium: signupUtm?.medium ?? null,
            utm_campaign: signupUtm?.campaign ?? null,
            utm_content: signupUtm?.content ?? null,
          });
        }
        linkAccountsMutation.mutate({ phoneNumber });
      } else {
        const errorMessage =
          response &&
          typeof response === "object" &&
          "error" in response &&
          typeof response.error === "string"
            ? response.error
            : tErr("invalidCodeDescription");
        toast.error(tErr("invalidCode"), { description: errorMessage });
      }
    },
    onError: (err: unknown) => {
      toast.error(tErr("codeVerificationFailed"), {
        description:
          (err instanceof Error ? err.message : null) ||
          tErr("codeVerificationDescription"),
      });
    },
  });

  // Linq status polling; the webhook on Linq's side flips the row to
  // VERIFIED when the user's reply lands. No-phone path: we look up by user.
  const messagesStatusQuery = api.user.getMessagesVerificationStatus.useQuery(
    {},
    {
      enabled: step === "qr" && !qrTimedOut,
      refetchInterval: 3000,
      // The QR flow puts the user on their phone by design, so the app-wide
      // default of pausing interval refetches while unfocused would stop the
      // poll for exactly as long as the hand-off takes.
      refetchIntervalInBackground: true,
      staleTime: 0,
      retry: false,
    },
  );

  // Background refetching still cannot run while the tab is suspended, which
  // is what mobile does once Messages takes the foreground.
  useEffect(() => {
    if (step !== "qr" || !linqToken) {
      return;
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void utils.user.getMessagesVerificationStatus.invalidate();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [step, linqToken, utils]);

  // Hard deadline on the QR, owned by the server's token TTL and delivered as
  // a duration so the device's clock offset cannot shift it. One authoritative
  // read before closing the flow: between the last poll tick and the deadline
  // the hand-off may have completed, and on resume-from-suspend the overdue
  // timeout and the first resumed tick race.
  // Deliberately not keyed on `step`: the sub-step round trip (fallback link
  // to "phone", then Back to QR) does not re-mint, so tearing the timer down
  // and re-arming it would grant the full window again on a token that keeps
  // its original expiry. The window belongs to the token, not to the view.
  const qrTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!linqToken || linqExpiresInMs === null) {
      return;
    }
    const expire = async () => {
      try {
        // `staleTime: 0` is required: `fetch` resolves from the cache while the
        // entry is fresh, and the app-wide default keeps it fresh for 5
        // minutes, so this would replay the poll's last "pending" instead of
        // asking the server the one question this read exists to ask.
        const status = await utils.user.getMessagesVerificationStatus.fetch(
          {},
          { staleTime: 0 },
        );
        if (status.status === "verified") {
          return;
        }
      } catch {
        // A failed re-check is not a verification; fall through and close.
      }
      setQrTimedOut(true);
    };
    qrTimeoutRef.current = setTimeout(() => void expire(), linqExpiresInMs);
    return () => {
      if (qrTimeoutRef.current) {
        clearTimeout(qrTimeoutRef.current);
      }
    };
  }, [linqToken, linqExpiresInMs, utils]);

  // On verified, the Linq row is already attached to the authed user. Just
  // navigate so the server-side router advances to the next step.
  useEffect(() => {
    if (messagesStatusQuery.data?.status !== "verified") {
      return;
    }
    if (!phoneCompletedFiredRef.current) {
      phoneCompletedFiredRef.current = true;
      posthog?.capture("onboarding_join_phone_completed", {
        method: "messages",
        utm_source: signupUtm?.source ?? null,
        utm_medium: signupUtm?.medium ?? null,
        utm_campaign: signupUtm?.campaign ?? null,
        utm_content: signupUtm?.content ?? null,
      });
    }
    const target = returnUrl
      ? `/join?returnUrl=${encodeURIComponent(returnUrl)}`
      : "/join";
    router.push(target);
    router.refresh();
  }, [messagesStatusQuery.data?.status, posthog, returnUrl, router]);

  /* ─────────────────────────────────────────────────────────────────── */
  /* Handlers                                                              */
  /* ─────────────────────────────────────────────────────────────────── */

  // Reached only via the "Verify with a code instead" fallback. The user
  // types their phone, we send a Twilio OTP, then advance to the code step.
  const handleContinueFromPhoneFallback = useCallback(() => {
    if (!phoneNumber) {
      setError("Enter your phone number to continue.");
      return;
    }
    if (!isValidPhoneNumber(phoneNumber)) {
      setError(
        "That phone number does not look right. Include the country and area code.",
      );
      return;
    }
    if (
      !isTwilioCountrySupported ||
      additionalVerificationRequired ||
      resendIn > 0
    ) {
      return;
    }
    setError(null);
    sendVerificationMutation.mutate({ phoneNumber });
  }, [
    phoneNumber,
    isTwilioCountrySupported,
    additionalVerificationRequired,
    resendIn,
    sendVerificationMutation,
  ]);

  // Click on the QR-page fallback link; just route to the phone-entry step.
  // The Twilio code path fires only after the user types their number there.
  const handleFallbackToCode = useCallback(() => {
    if (additionalVerificationRequired) {
      return;
    }
    setError(null);
    setStep("phone");
  }, [additionalVerificationRequired]);

  const handleVerifyCode = useCallback(() => {
    if (!verificationData || !phoneNumber || verificationCode.length !== 6) {
      return;
    }
    verifyCodeMutation.mutate({
      phoneNumber,
      code: verificationCode,
      verificationSid: verificationData.verificationSid,
      verifyServiceSid: verificationData.verifyServiceSid,
    });
  }, [phoneNumber, verificationCode, verificationData, verifyCodeMutation]);

  const handleRegenerateQr = useCallback(() => {
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      return;
    }
    setQrTimedOut(false);
    setMintFailed(false);
    setLinqToken(null);
    setLinqNumber(null);
    // The server reuses an active token rather than extending it, so the next
    // response may carry the deadline this attempt already reached.
    setLinqExpiresInMs(null);
    lastMintedPhoneRef.current = phoneNumber;
    startMessages.mutate({ phoneNumber });
  }, [phoneNumber, startMessages]);

  const isVerifying =
    verifyCodeMutation.isPending || linkAccountsMutation.isPending;

  // Step indicator: 3 segments (sources done → verification → … ).
  // QR is the entry, so segment 2 is wide on mount. The phone fallback +
  // code entry both live under "verification" so the bar doesn't shift.

  return (
    <div className="join-phone bg-background relative flex min-h-[100dvh] flex-col items-center justify-start px-5 pt-14 pb-10 sm:min-h-screen sm:flex-row sm:justify-center sm:px-10 sm:py-16">
      <style jsx global>{`
        .join-phone .PhoneInput {
          height: 48px;
          border: 1px solid var(--border);
          border-radius: 8px;
          overflow: hidden;
          background: var(--muted);
          display: flex;
          align-items: stretch;
          transition:
            border-color 150ms,
            background 150ms,
            box-shadow 150ms;
        }
        .join-phone .PhoneInput:focus-within {
          border-color: #0a06f7;
          background: var(--muted);
          box-shadow: 0 0 0 3px rgba(10, 6, 247, 0.1);
        }
        .join-phone .PhoneInput input {
          height: 100% !important;
          border: 0 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          background: transparent !important;
          padding: 0 14px !important;
          outline: none !important;
          font-size: 16px;
          color: var(--foreground);
          flex: 1;
        }
        @media (min-width: 640px) {
          .join-phone .PhoneInput input {
            font-size: 15px;
          }
        }
        .join-phone .PhoneInput input:focus {
          border: 0 !important;
          box-shadow: none !important;
          outline: none !important;
        }
        .join-phone .PhoneInput button,
        .join-phone .PhoneInputCountry {
          height: 100% !important;
          border: 0 !important;
          border-radius: 0 !important;
          border-right: 1px solid var(--border) !important;
          background: transparent !important;
          padding: 0 12px !important;
          box-shadow: none !important;
          outline: none !important;
        }
      `}</style>

      <JoinStepBar active="phone" />

      {step === "phone" && (
        <PhoneEntry
          phoneNumber={phoneNumber}
          onPhoneNumberChange={(val) => {
            setPhoneNumber(val);
            setError(null);
          }}
          onCountryChange={(c) => {
            setSelectedCountry(c);
          }}
          countryAvailable={countryAvailable}
          channelReachable={isTwilioCountrySupported}
          selectedCountry={selectedCountry}
          error={error}
          onContinue={handleContinueFromPhoneFallback}
          onBackToQr={() => {
            setStep("qr");
            setError(null);
          }}
          isLoading={sendVerificationMutation.isPending}
          resendIn={resendIn}
        />
      )}

      {step === "qr" && (
        <QrScan
          isMobile={isMobile}
          token={linqToken}
          linqNumber={linqNumber}
          preferredScheme={preferredScheme}
          timedOut={qrTimedOut}
          onRegenerate={handleRegenerateQr}
          mintFailed={mintFailed}
          regenerating={startMessages.isPending}
          onFallbackToCode={handleFallbackToCode}
          canFallbackToTwilio={
            isTwilioCountrySupported && !additionalVerificationRequired
          }
          showCodeFallback={showCodeFallback}
          phoneNumber={phoneNumber}
          onPhoneNumberChange={(val) => {
            setPhoneNumber(val);
            setVerificationData(null);
            setVerificationCode("");
            setError(null);
          }}
          onCountryChange={(c) => {
            setSelectedCountry(c);
          }}
          countryAvailable={countryAvailable}
          channelReachable={linqCountrySupported}
          selectedCountry={selectedCountry}
          phoneReady={isPhoneReadyForLinq}
          error={error}
          additionalVerificationRequired={additionalVerificationRequired}
          onUseExistingCode={
            verificationData
              ? () => {
                  setStep("code");
                }
              : undefined
          }
        />
      )}

      {step === "code" && (
        <CodeEntry
          phoneNumber={phoneNumber ?? null}
          verificationCode={verificationCode}
          onVerificationCodeChange={setVerificationCode}
          error={error}
          additionalVerificationRequired={additionalVerificationRequired}
          onBackToQr={() => {
            setStep("qr");
          }}
          onVerify={handleVerifyCode}
          isVerifying={isVerifying}
          resendIn={resendIn}
          onResend={() => {
            if (
              phoneNumber &&
              !additionalVerificationRequired &&
              resendIn === 0
            ) {
              sendVerificationMutation.mutate({ phoneNumber });
            }
          }}
          isResending={sendVerificationMutation.isPending}
          onChangeNumber={() => {
            setStep("phone");
            setVerificationCode("");
            setVerificationData(null);
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Phone number entry                                                   */
/* ─────────────────────────────────────────────────────────────────── */

interface PhoneEntryProps {
  phoneNumber: PhoneValue | undefined;
  onPhoneNumberChange: (val: PhoneValue | undefined) => void;
  onCountryChange: (c: Country) => void;
  /** Policy: Terac is open in this country. Drives the "not available" copy. */
  countryAvailable: boolean;
  /** Capability: this screen's channel can reach the country. */
  channelReachable: boolean;
  selectedCountry: Country;
  error: string | null;
  onContinue: () => void;
  onBackToQr: () => void;
  isLoading: boolean;
  resendIn: number;
}

function PhoneEntry({
  phoneNumber,
  onPhoneNumberChange,
  onCountryChange,
  countryAvailable,
  channelReachable,
  selectedCountry,
  error,
  onContinue,
  onBackToQr,
  isLoading,
  resendIn,
}: PhoneEntryProps) {
  const t = useTranslations("Join.phone");
  const locale = useLocale();
  const isCountrySupported = countryAvailable && channelReachable;
  return (
    <div className="flex w-full max-w-[420px] flex-1 flex-col gap-[22px] sm:flex-none">
      <div>
        <button
          type="button"
          onClick={onBackToQr}
          className="hover:text-foreground mb-3 inline-flex cursor-pointer items-center gap-1.5 text-[12.5px] text-[#606060] transition-colors dark:text-neutral-400"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          {t("backToQr")}
        </button>
        <h1 className="text-foreground m-0 text-[26px] leading-[1.2] font-medium tracking-[-0.02em]">
          {t("entryHeading")}
        </h1>
        <p className="mt-2 text-[14px] leading-[1.5] text-[#606060] dark:text-neutral-400">
          {t("entryDescription")}
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-[22px] sm:flex-none">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11px] tracking-[0.04em] text-[#606060] uppercase dark:text-neutral-400">
            {t("phoneNumberLabel")}
          </span>
          <PhoneInput
            value={phoneNumber}
            onChange={onPhoneNumberChange}
            onCountryChange={(c) => {
              if (c) {
                onCountryChange(c);
              }
            }}
            placeholder="(555) 123-4567"
            defaultCountry="US"
            disabled={isLoading}
          />
        </div>

        {!countryAvailable && (
          <p className="text-[13px] leading-[1.5] text-neutral-500 dark:text-neutral-400">
            {t("countryUnavailable", {
              country: countryName(locale, selectedCountry, t("yourCountry")),
            })}
          </p>
        )}

        {countryAvailable && !channelReachable && (
          <p className="text-[13px] leading-[1.5] text-neutral-500 dark:text-neutral-400">
            {t("cannotTextCountry", {
              country: countryName(locale, selectedCountry, t("yourCountry")),
            })}
          </p>
        )}

        {error && isCountrySupported && (
          <p className="text-destructive text-[13px] leading-[1.5]">{error}</p>
        )}

        <div className="flex-1 sm:hidden" />

        <button
          type="button"
          onClick={onContinue}
          disabled={isLoading || !isCountrySupported || resendIn > 0}
          className="bg-marketing-blue hover:bg-marketing-blue-dark inline-flex h-[48px] w-full cursor-pointer items-center justify-center gap-2.5 rounded-full px-7 text-[15px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:h-[46px]"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("sendingCode")}
            </>
          ) : (
            <>
              {resendIn > 0
                ? t("resendIn", { countdown: formatCountdown(resendIn) })
                : t("sendCode")}
              <ArrowSvg />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* QR scan (Linq messages-based verification)                            */
/* ─────────────────────────────────────────────────────────────────── */

interface QrScanProps {
  isMobile: boolean;
  token: string | null;
  linqNumber: string | null;
  preferredScheme: "imessage" | "sms";
  timedOut: boolean;
  onRegenerate: () => void;
  /** The mint errored, so there is no token and no timer will ever fire. */
  mintFailed: boolean;
  regenerating: boolean;
  onFallbackToCode: () => void;
  canFallbackToTwilio: boolean;
  /** Whether to render the exit right now; `canFallbackToTwilio` is only
   *  whether Twilio could serve it at all, which the country copy reads. */
  showCodeFallback: boolean;
  phoneNumber: PhoneValue | undefined;
  onPhoneNumberChange: (val: PhoneValue | undefined) => void;
  onCountryChange: (c: Country) => void;
  /** Policy: Terac is open in this country. Drives the "not available" copy. */
  countryAvailable: boolean;
  /** Capability: Linq can deliver the token to this country. */
  channelReachable: boolean;
  selectedCountry: Country;
  phoneReady: boolean;
  error: string | null;
  additionalVerificationRequired: boolean;
  onUseExistingCode?: () => void;
}

function QrScan({
  isMobile,
  token,
  linqNumber,
  preferredScheme,
  timedOut,
  onRegenerate,
  mintFailed,
  regenerating,
  onFallbackToCode,
  canFallbackToTwilio,
  showCodeFallback,
  phoneNumber,
  onPhoneNumberChange,
  onCountryChange,
  countryAvailable,
  channelReachable,
  selectedCountry,
  phoneReady,
  error,
  additionalVerificationRequired,
  onUseExistingCode,
}: QrScanProps) {
  const t = useTranslations("Join.phone");
  const locale = useLocale();
  const isCountrySupported = countryAvailable && channelReachable;
  const ready = Boolean(token && linqNumber);
  const qrUrl = ready
    ? buildDeepLink(preferredScheme, linqNumber!, token!)
    : null;
  const mobileUrl = ready
    ? buildDeepLink(detectIOS() ? "imessage" : "sms", linqNumber!, token!)
    : null;

  const smsBody = token ? `Hi Terac! My code is ${token}.` : "";

  // Mobile: track whether the user has tapped Verify so the status flips
  // from "Ready when you are" → "Waiting for your message" with the pulse.
  const [tapped, setTapped] = useState(false);

  return (
    <div className="flex w-full max-w-[460px] flex-1 flex-col gap-6 sm:flex-none">
      {/* Header; left-aligned per design (the QR card centers itself). */}
      <div>
        <h1 className="text-foreground m-0 text-[26px] leading-[1.2] font-medium tracking-[-0.025em] sm:text-[28px] sm:leading-[1.15]">
          {t("qrHeading")}
        </h1>
        <p className="mt-2.5 text-[14px] leading-[1.5] text-[#606060] sm:text-[14.5px] sm:leading-[1.55] dark:text-neutral-400">
          {isMobile ? t("qrDescriptionMobile") : t("qrDescriptionDesktop")}
        </p>
      </div>
      {additionalVerificationRequired && (
        <div role="alert" className="space-y-2 text-sm">
          <p>
            {friendlyPhoneError(
              PhoneVerificationError.ADDITIONAL_VERIFICATION_REQUIRED,
            )}
          </p>
          {onUseExistingCode && (
            <button
              type="button"
              onClick={onUseExistingCode}
              className="block underline"
            >
              {t("enterExistingCode")}
            </button>
          )}
        </div>
      )}

      {/* Phone field; bind the token to this exact number before minting; the
          webhook rejects any other sender. */}
      <div className="flex flex-col gap-2">
        <span className="font-mono text-[11px] tracking-[0.04em] text-[#606060] uppercase dark:text-neutral-400">
          {t("phoneNumberLabel")}
        </span>
        <PhoneInput
          value={phoneNumber}
          onChange={onPhoneNumberChange}
          onCountryChange={(c) => {
            if (c) {
              onCountryChange(c);
            }
          }}
          placeholder="(555) 123-4567"
          defaultCountry="US"
        />
        {!countryAvailable && (
          <p className="text-[13px] leading-[1.5] text-neutral-500 dark:text-neutral-400">
            {t("countryUnavailable", {
              country: countryName(locale, selectedCountry, t("yourCountry")),
            })}
          </p>
        )}
        {countryAvailable && !channelReachable && (
          <p className="text-[13px] leading-[1.5] text-neutral-500 dark:text-neutral-400">
            {t(
              canFallbackToTwilio
                ? "cannotDeliverFallback"
                : "cannotDeliverNoFallback",
              {
                country: countryName(locale, selectedCountry, t("yourCountry")),
              },
            )}
          </p>
        )}
        {error && isCountrySupported && (
          <p className="text-destructive text-[13px] leading-[1.5]">{error}</p>
        )}
      </div>

      {!phoneReady && (
        <p className="text-[13px] leading-[1.5] text-[#606060] dark:text-neutral-400">
          {t("enterNumberFirst")}
        </p>
      )}

      {/* ── Mobile: iMessage-compose preview card ─────────────────────── */}
      {phoneReady && isMobile && (
        <div className="border-border bg-muted relative flex flex-col gap-3 rounded-[16px] border px-4 pt-4 pb-[18px]">
          <div className="border-border flex items-center gap-2.5 border-b pb-3 font-mono text-[11px] tracking-[0.12em] uppercase">
            <span className="text-[#606060] dark:text-neutral-400">
              {t("smsTo")}
            </span>
            <span className="text-foreground text-[13px] font-medium tracking-normal normal-case">
              {t("smsRecipient")}
            </span>
          </div>
          {/* Outgoing chat bubble; iMessage-style tail (bottom-right 4px). */}
          <div className="flex justify-end">
            <div
              className="bg-marketing-blue max-w-[80%] px-3.5 py-2.5 font-mono text-[14px] tracking-[0.04em] text-white"
              style={{ borderRadius: 18, borderBottomRightRadius: 4 }}
            >
              {smsBody || t("generatingCode")}
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile status pill (sits centered below the iMessage card) ── */}
      {phoneReady && isMobile && !timedOut && (
        <div className="flex justify-center">
          <StatusPill ready={ready} variant="mobile" tapped={tapped} />
        </div>
      )}

      {/* ── Desktop: QR card with viewfinder corner brackets ──────────── */}
      {phoneReady && !isMobile && (
        <div className="border-border bg-muted relative flex flex-col items-center gap-[18px] rounded-[16px] border px-7 pt-7 pb-[22px]">
          <CornerBracket position="tl" />
          <CornerBracket position="tr" />
          <CornerBracket position="bl" />
          <CornerBracket position="br" />

          {/* QR sub-card. The white fill is deliberately not themed: the code
              is rendered in marketing-blue, so it needs a light quiet zone to
              stay scannable when the rest of the page is in dark mode. */}
          <div className="border-border relative rounded-[12px] border bg-white p-3.5">
            <div className="relative" style={{ width: 196, height: 196 }}>
              {ready ? (
                <div
                  className={cn(
                    "transition-opacity duration-300",
                    timedOut && "opacity-30 grayscale",
                  )}
                >
                  <StyledQR
                    value={qrUrl!}
                    size={196}
                    color="#0a06f7"
                    logoUrl="/brand/logo.svg"
                  />
                </div>
              ) : (
                <QrPlaceholder size={196} />
              )}
              {timedOut && (
                <div className="bg-muted/85 absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-md backdrop-blur-[1px]">
                  <div className="text-foreground text-[14px] font-medium">
                    {t("codeExpired")}
                  </div>
                  <div className="text-[12px] text-[#606060] dark:text-neutral-400">
                    {t("regenerateToRetry")}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status pill INSIDE the QR card per design. */}
          {!timedOut && <StatusPill ready={ready} variant="desktop" />}
        </div>
      )}

      {phoneReady && (timedOut || mintFailed) && (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={regenerating}
          className="bg-marketing-blue hover:bg-marketing-blue-dark inline-flex h-[46px] cursor-pointer items-center justify-center gap-2.5 self-center rounded-full px-7 text-[15px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        >
          {regenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("regenerating")}
            </>
          ) : (
            t("regenerateCode")
          )}
        </button>
      )}

      <div className="flex-1 sm:hidden" />

      {phoneReady && isMobile && ready && !timedOut && mobileUrl && (
        <a
          href={mobileUrl}
          onClick={() => {
            setTapped(true);
          }}
          className="bg-marketing-blue hover:bg-marketing-blue-dark inline-flex h-[48px] w-full cursor-pointer items-center justify-center gap-2.5 rounded-full px-7 text-[15px] font-medium tracking-[-0.01em] text-white transition-colors"
        >
          {t("openMessages")}
          <ArrowSvg />
        </a>
      )}

      <p className="text-center text-sm">
        {t("cannotUseMessages")}{" "}
        <a href="mailto:support@terac.com" className="underline">
          {t("contactSupport")}
        </a>
        .
      </p>

      {showCodeFallback && (
        <div className="fallback-reveal text-center text-[13px] text-[#606060] sm:pt-1 sm:text-[13.5px] dark:text-neutral-400">
          {t("notWorking")}{" "}
          <button
            type="button"
            onClick={onFallbackToCode}
            className="text-marketing-blue dark:text-marketing-blue-light cursor-pointer font-medium underline decoration-[rgba(10,6,247,0.35)] underline-offset-[3px]"
          >
            {t("verifyWithCode")}
          </button>
        </div>
      )}

      <style jsx>{`
        @keyframes fallbackReveal {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fallbackFade {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .fallback-reveal {
          animation: fallbackReveal 260ms cubic-bezier(0.23, 1, 0.32, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .fallback-reveal {
            animation-name: fallbackFade;
          }
        }
        @keyframes qrPing {
          0% {
            transform: scale(1);
            opacity: 0.5;
          }
          100% {
            transform: scale(2.4);
            opacity: 0;
          }
        }
        @keyframes qrDot {
          0%,
          80%,
          100% {
            opacity: 0.25;
          }
          40% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

/* Viewfinder corner; 14×14 with two adjacent borders blue, the other two
   transparent. Renders at the corners of the QR card. */
function CornerBracket({ position }: { position: "tl" | "tr" | "bl" | "br" }) {
  const isTop = position.startsWith("t");
  const isLeft = position.endsWith("l");
  return (
    <span
      aria-hidden
      className="border-marketing-blue dark:border-marketing-blue-light pointer-events-none absolute h-[14px] w-[14px]"
      style={{
        top: isTop ? 14 : undefined,
        bottom: !isTop ? 14 : undefined,
        left: isLeft ? 14 : undefined,
        right: !isLeft ? 14 : undefined,
        borderTopWidth: isTop ? 1 : 0,
        borderBottomWidth: !isTop ? 1 : 0,
        borderLeftWidth: isLeft ? 1 : 0,
        borderRightWidth: !isLeft ? 1 : 0,
      }}
    />
  );
}

interface StatusPillProps {
  ready: boolean;
  variant: "desktop" | "mobile";
  tapped?: boolean;
}

function StatusPill({ ready, variant, tapped = false }: StatusPillProps) {
  const t = useTranslations("Join.phone");
  // Desktop: always pulsing blue (we're waiting for the user's message).
  // Mobile: faint dot before the user taps Verify; pulsing blue after.
  const isWaiting = variant === "desktop" || tapped;
  const dotColor = isWaiting ? "bg-marketing-blue" : "bg-neutral-500";
  const label = !ready
    ? t("generatingCode")
    : variant === "mobile" && !tapped
      ? t("statusReady")
      : t("statusWaiting");

  return (
    <div className="flex items-center gap-2.5 font-mono text-[11px] tracking-[0.1em] text-[#606060] uppercase dark:text-neutral-400">
      <span className="relative inline-flex h-[7px] w-[7px]">
        <span className={cn("h-[7px] w-[7px] rounded-full", dotColor)} />
        {isWaiting && (
          <span
            className="bg-marketing-blue absolute -inset-[3px] rounded-full opacity-40"
            style={{
              animation: "qrPing 1.6s cubic-bezier(0,0,0.2,1) infinite",
            }}
          />
        )}
      </span>
      {label}
      {/* Three-dot trailing indicator only when actively waiting. */}
      {isWaiting && ready && (
        <span className="inline-flex gap-[2px]">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-[3px] w-[3px] rounded-full bg-neutral-500"
              style={{
                animation: `qrDot 1.4s ease-in-out ${i * 0.15}s infinite`,
              }}
            />
          ))}
        </span>
      )}
    </div>
  );
}

function QrPlaceholder({ size }: { size: number }) {
  const t = useTranslations("Join.phone");
  const cols = 13;
  const cells = Array.from({ length: cols * cols });
  return (
    <div
      aria-label={t("generatingQr")}
      className="border-marketing-blue/20 dark:border-marketing-blue-light/20 bg-marketing-blue/5 grid overflow-hidden rounded-md border p-2"
      style={{
        width: size,
        height: size,
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${cols}, 1fr)`,
        gap: 3,
      }}
    >
      {cells.map((_, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;
        const delay = (row + col) * 90;
        return (
          <div
            key={`cell-${row}-${col}`}
            className="bg-marketing-blue animate-pulse rounded-[2px]"
            style={{ animationDelay: `${delay}ms` }}
          />
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* SMS code fallback (Twilio path)                                       */
/* ─────────────────────────────────────────────────────────────────── */

interface CodeEntryProps {
  phoneNumber: string | null;
  verificationCode: string;
  onVerificationCodeChange: (val: string) => void;
  onVerify: () => void;
  isVerifying: boolean;
  resendIn: number;
  onResend: () => void;
  isResending: boolean;
  onChangeNumber: () => void;
  error: string | null;
  additionalVerificationRequired: boolean;
  onBackToQr: () => void;
}

function CodeEntry({
  phoneNumber,
  verificationCode,
  onVerificationCodeChange,
  onVerify,
  isVerifying,
  resendIn,
  onResend,
  isResending,
  onChangeNumber,
  error,
  additionalVerificationRequired,
  onBackToQr,
}: CodeEntryProps) {
  const t = useTranslations("Join.phone");
  return (
    <div className="flex w-full max-w-[420px] flex-1 flex-col gap-[22px] sm:flex-none">
      <div>
        <h1 className="text-foreground m-0 text-[26px] leading-[1.2] font-medium tracking-[-0.02em]">
          {t("codeHeading")}
        </h1>
        <p className="mt-2 text-[14px] leading-[1.5] text-[#606060] dark:text-neutral-400">
          {t("codeSentTo", { phone: phoneNumber ?? t("yourPhone") })}{" "}
          <button
            type="button"
            onClick={onChangeNumber}
            disabled={isVerifying}
            className="text-marketing-blue dark:text-marketing-blue-light inline cursor-pointer font-medium underline decoration-[rgba(10,6,247,0.35)] underline-offset-[3px] disabled:cursor-not-allowed sm:hidden"
          >
            {t("changeNumber")}
          </button>
        </p>
      </div>
      {error && (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
      <button type="button" onClick={onBackToQr} className="text-sm underline">
        {t("verifyWithMessages")}
      </button>

      <div className="flex flex-1 flex-col gap-6 sm:flex-none">
        <OTPInput
          value={verificationCode}
          onChange={onVerificationCodeChange}
          onComplete={(value) => {
            if (value.length === 6) {
              setTimeout(() => {
                onVerify();
              }, 100);
            }
          }}
          maxLength={6}
          disabled={isVerifying}
          containerClassName="flex items-center justify-between gap-2 has-disabled:opacity-50 sm:justify-center"
          render={({ slots }) => (
            <>
              {slots.map((slot, idx) => (
                // eslint-disable-next-line react/no-array-index-key -- static 6-digit OTP
                <OtpCell key={idx} {...slot} />
              ))}
            </>
          )}
        />

        {!additionalVerificationRequired && (
          <div className="flex items-center justify-center gap-1.5 text-[13px]">
            <span className="text-[#606060] dark:text-neutral-400">
              {t("didntGetIt")}
            </span>
            {resendIn > 0 ? (
              <span className="font-mono text-neutral-500 tabular-nums dark:text-neutral-400">
                {t("resendIn", { countdown: formatCountdown(resendIn) })}
              </span>
            ) : (
              <button
                type="button"
                onClick={onResend}
                disabled={isResending}
                className="text-marketing-blue dark:text-marketing-blue-light hover:text-marketing-blue-dark cursor-pointer font-medium transition-colors disabled:cursor-not-allowed"
              >
                {isResending ? t("resending") : t("resendCode")}
              </button>
            )}
          </div>
        )}

        <div className="flex-1 sm:hidden" />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:pt-1">
          <button
            type="button"
            onClick={onChangeNumber}
            disabled={isVerifying}
            className="hover:text-foreground hidden h-10 cursor-pointer items-center text-[14px] font-medium text-[#606060] transition-colors disabled:cursor-not-allowed sm:inline-flex dark:text-neutral-400"
          >
            {t("changeNumber")}
          </button>
          <button
            type="button"
            onClick={onVerify}
            disabled={isVerifying || verificationCode.length !== 6}
            className="bg-marketing-blue hover:bg-marketing-blue-dark inline-flex h-[48px] w-full cursor-pointer items-center justify-center gap-2.5 rounded-full px-7 text-[15px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 sm:h-[46px] sm:w-auto"
          >
            {isVerifying ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("verifying")}
              </>
            ) : (
              <>
                {t("verify")}
                <ArrowSvg />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ArrowSvg() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      aria-hidden
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function OtpCell({ char, hasFakeCaret, isActive }: SlotProps) {
  return (
    <div
      className={cn(
        "relative flex h-[60px] w-0 flex-1 items-center justify-center rounded-lg border bg-muted text-[24px] font-medium tracking-[-0.01em] text-foreground transition-[border-color,box-shadow] duration-150 ease-out sm:h-[52px] sm:w-12 sm:flex-none sm:text-[20px]",
        isActive
          ? "border-marketing-blue dark:border-marketing-blue-light shadow-[0_0_0_3px_rgba(10,6,247,0.1)]"
          : "border-border",
      )}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="bg-marketing-blue h-6 w-px animate-pulse" />
        </div>
      )}
    </div>
  );
}
